import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['overlay', 'title', 'message', 'confirmButton', 'panel']

  connect () {
    this.pendingForm = null
    this.pendingSubmitter = null
    this.skipNextAsk = false
    this.handleKeydown = (event) => {
      if (event.key === 'Escape') this.cancel()
    }
  }

  disconnect () {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  ask (event) {
    // Programmatic requestSubmit() can re-fire click on the submitter; let that through.
    if (this.skipNextAsk) {
      this.skipNextAsk = false
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const form = event.target.closest('form')
    if (!form) return

    // Click handlers run before HTML5 validation. Check first so required fields
    // (e.g. rejection reason) surface native errors instead of a silent no-op after Confirm.
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      form.reportValidity()
      return
    }

    const trigger = event.target.closest('[data-confirm-title]') || form

    const title = trigger.dataset.confirmTitle || 'Are you sure?'
    const message = trigger.dataset.confirmMessage || ''
    const confirmLabel = trigger.dataset.confirmButton || 'Confirm'
    const destructive = trigger.dataset.confirmDestructive !== 'false'

    this.pendingForm = form
    this.pendingSubmitter = this.submitTriggerFor(event.target)
    this.titleTarget.textContent = title
    this.messageTarget.textContent = message
    this.messageTarget.classList.toggle('hidden', !message)
    this.confirmButtonTarget.textContent = confirmLabel

    this.confirmButtonTarget.className = destructive
      ? 'inline-flex flex-1 items-center justify-center rounded-xl bg-rose-600 px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-rose-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2 sm:flex-none sm:px-6'
      : 'inline-flex flex-1 items-center justify-center rounded-xl bg-gradient-brand px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 sm:flex-none sm:px-6'

    this.overlayTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
    document.addEventListener('keydown', this.handleKeydown)
    this.confirmButtonTarget.focus()
  }

  confirm () {
    if (this.pendingForm) {
      const form = this.pendingForm
      const submitter = this.pendingSubmitter
      this.pendingForm = null
      this.pendingSubmitter = null
      this.skipNextAsk = true
      try {
        if (submitter) {
          form.requestSubmit(submitter)
        } else {
          form.requestSubmit()
        }
      } finally {
        queueMicrotask(() => { this.skipNextAsk = false })
      }
    }
    this.close()
  }

  cancel () {
    this.pendingForm = null
    this.pendingSubmitter = null
    this.close()
  }

  backdropClick (event) {
    if (event.target === this.overlayTarget) this.cancel()
  }

  stopPropagation (event) {
    event.stopPropagation()
  }

  close () {
    this.overlayTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    document.removeEventListener('keydown', this.handleKeydown)
  }

  submitTriggerFor (target) {
    if (!target) return null
    if (target.matches('input[type="submit"], button[type="submit"], button:not([type])')) return target
    return target.closest('input[type="submit"], button[type="submit"]')
  }
}
